import { useState, useEffect } from 'react';
import { FaTrophy, FaFire, FaMedal, FaStar, FaCrown, FaTimes } from 'react-icons/fa';
import '../../styles/badge-notification.css';

/**
 * Global Badge Earned Notification
 * Displays an attractive animated notification when a badge is earned
 * Shows anywhere on the website with confetti and celebration effects
 */
const BadgeEarnedNotification = ({ badge, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState([]);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 100);

    // Generate confetti
    const pieces = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: getRandomColor()
    }));
    setConfettiPieces(pieces);

    // Auto-close after 8 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 500);
  };

  const getRandomColor = () => {
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getBadgeIcon = (category) => {
    if (category?.toLowerCase().includes('streak')) return FaFire;
    if (category?.toLowerCase().includes('problem')) return FaTrophy;
    if (category?.toLowerCase().includes('challenge')) return FaMedal;
    if (category?.toLowerCase().includes('special')) return FaCrown;
    return FaStar;
  };

  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      case 'platinum': return '#E5E4E2';
      case 'diamond': return '#B9F2FF';
      case 'easy': return '#22c55e';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      case 'legendary': return '#a855f7';
      default: return '#6b7280';
    }
  };

  const Icon = getBadgeIcon(badge?.category);
  const tierColor = getTierColor(badge?.tier);
  const isLegendary = badge?.tier?.toLowerCase() === 'legendary';

  return (
    <>
      {/* Confetti */}
      {confettiPieces.map(piece => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: `${piece.left}%`,
            backgroundColor: piece.color,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`
          }}
        />
      ))}

      {/* Notification Card */}
      <div className={`badge-earned-notification ${isVisible ? 'visible' : ''}`}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="badge-notification-close"
          aria-label="Close notification"
        >
          <FaTimes size={16} />
        </button>

        {/* Celebration Header */}
        <div className="badge-notification-header">
          <div className="celebration-emoji">🎉</div>
          <h3 className="badge-notification-title">Badge Unlocked!</h3>
        </div>

        {/* Badge Display */}
        <div className="badge-notification-content">
          {/* Badge Icon with 3D Effect */}
          <div 
            className={`badge-notification-icon ${isLegendary ? 'legendary' : ''}`}
            style={{
              background: `linear-gradient(145deg, ${tierColor}, ${tierColor}dd, ${tierColor}aa)`,
              boxShadow: `0 8px 32px ${tierColor}60, 0 0 60px ${tierColor}40`
            }}
          >
            <Icon size={48} color="#ffffff" />
            
            {/* Sparkle Effects */}
            <div className="sparkle sparkle-1">✨</div>
            <div className="sparkle sparkle-2">✨</div>
            <div className="sparkle sparkle-3">✨</div>
            <div className="sparkle sparkle-4">✨</div>
          </div>

          {/* Badge Info */}
          <div className="badge-notification-info">
            <h4 className="badge-name">{badge?.name || 'New Badge'}</h4>
            <p className="badge-description">{badge?.description || 'You earned a new achievement!'}</p>
            
            {/* Tier Badge */}
            <div className="badge-tier-container">
              <span 
                className="badge-tier-label"
                style={{
                  backgroundColor: `${tierColor}20`,
                  color: tierColor,
                  borderColor: `${tierColor}40`
                }}
              >
                {badge?.tier || 'Special'}
              </span>
              {isLegendary && (
                <span className="legendary-indicator">
                  <FaCrown size={12} /> Legendary
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar Animation */}
        <div className="badge-notification-progress">
          <div 
            className="badge-notification-progress-bar"
            style={{ backgroundColor: tierColor }}
          />
        </div>

        {/* Particle Effects */}
        <div className="particle-container">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${50 + (Math.random() - 0.5) * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                backgroundColor: tierColor
              }}
            />
          ))}
        </div>

        {/* Glow Effect */}
        <div 
          className="badge-notification-glow"
          style={{ backgroundColor: `${tierColor}30` }}
        />
      </div>
    </>
  );
};

export default BadgeEarnedNotification;
